/**
 * m46-parking has no endpoint that lists OffenceCode rows — POST
 * /contraventions accepts only the literal `code` string (e.g.
 * 'NO_PAYMENT'), resolved server-side to whichever OffenceCode row is
 * currently effective for it (see service/contraventions.ts's
 * issueContravention()). So this portal hardcodes the same four codes/
 * labels the mobile app's officer mode uses, per this build's own task
 * brief — this is a deliberate, spec-given choice, not a stand-in for a
 * missing endpoint.
 *
 * A Contravention row itself only ever exposes the resolved
 * `offenceCodeId` (an opaque cuid) — there is no endpoint to turn that back
 * into a code/label. rememberOffenceCode/getRememberedOffenceLabel below are
 * a client-side-only convenience, keyed by contraventionId in localStorage,
 * so a notice issued (or viewed) from THIS browser shows a friendly label
 * instead of a raw id; a notice issued elsewhere still shows the raw
 * offenceCodeId (see pages/contraventions/ContraventionDetail.tsx) — this is
 * a UI nicety, not a real lookup, and is documented as such at both call
 * sites.
 */
export interface OffenceCodeOption {
  code: string;
  label: string;
}

export const OFFENCE_CODES: OffenceCodeOption[] = [
  { code: 'NO_PAYMENT', label: 'No valid payment' },
  { code: 'EXPIRED_SESSION', label: 'Expired session' },
  { code: 'YELLOW_LINE', label: 'Yellow no-waiting line' },
  { code: 'DISABLED_BAY_NO_BADGE', label: 'Disabled bay, no badge' },
];

export function offenceLabel(code: string): string {
  return OFFENCE_CODES.find((o) => o.code === code)?.label ?? code;
}

const STORAGE_KEY = 'parking-portal.offenceCodeByContraventionId';

function readMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export function rememberOffenceCode(contraventionId: string, code: string): void {
  const map = readMap();
  map[contraventionId] = code;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

/** Returns a friendly label if this browser issued/viewed this notice
 * before, otherwise null — caller should fall back to the raw offenceCodeId. */
export function getRememberedOffenceLabel(contraventionId: string): string | null {
  const code = readMap()[contraventionId];
  return code ? offenceLabel(code) : null;
}
